import React, { useEffect, useState } from "react";
import { Grid, Box, Button, Typography, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import StackedBarChart from "../../components/StackedBarChart/StackedBarChart";
import CustomPieChart from "../DonutChart/DonutChart";
import StpTrendsLineChart from "../LineChart/StpTrendsLineChart";
import AgingTable from "../AgingTable/AgingTable";
import TrendsLineChartForInvoiceComplexity from "../LineChart/TrendLineChartForInvoiceComplexity";
import { CircularProgress } from "@mui/material";

const complexityTypes = [
  { value: "Single_Po", label: "Single PO" },
  { value: "Multi_Po", label: "Multi PO" },
  { value: "Non_Po", label: "Non PO" },
  { value: "Yet_To_Be_Extracted", label: "Yet To Be Extracted" },
];


export default function InvoiceComplexityTab({ data, onApplyInvComplexRptFilter, onClear, initialFilters, isPending, invoiceStatusListData }) {
  const [invoicesIngByStatus, setInvoicesIng] = useState([]);
  const [dataByBusinessUnit, setDataByBusinessUnit] = useState([]);
  const [invComplexBySupplier, setInvComplexBySupplier] = useState([]);
  const [invComplexRptTrends, setInvComplexRptTrends] = useState([]);
  const [invComplexExeptionList, setInvComplexExeptionList] = useState([])
  const [invComplexityRptFilters, setInvComplexityRptFilters] = useState({
    complexity_type: initialFilters?.complexity_type || [],
    invoice_status: initialFilters?.invoice_status || []
  })




  const dataByBU = [
    { date: '29 Aug', BU1: 10, BU2: 150, BU3: 0, BU4: 50 },
    { date: '30 Aug', BU1: 250, BU2: 100, BU3: 200, BU9: 50 },
    { date: '31 Aug', BU1: 200, BU2: 200, BU8: 150, BU10: 50 },
    { date: '1 Sep', BU1: 300, BU2: 100, BU7: 100, BU4: 50 },
    { date: '2 Sep', BU1: 250, BU2: 150, BU8: 150, BU4: 50 },
    { date: '3 Sep', BU1: 200, BU2: 100, BU6: 200, BU10: 50 },
    { date: '4 Sep', BU1: 150, BU2: 150, BU5: 250, BU9: 50 },
  ];


  useEffect(() => {
    setInvComplexityRptFilters({
      complexity_type: initialFilters?.complexity_type || [],
      invoice_status: initialFilters?.invoice_status || []
    })

  }, [initialFilters])


  useEffect(() => {
    if (data?.total_by_complexity && Object.keys(data.total_by_complexity)?.length) {
      const pieData = Object.entries(data.total_by_complexity).map(([name, value], index) => ({
        id: index,
        value: value,
        label: name.charAt(0).toUpperCase()+ name.slice(1).replaceAll("_"," ").replace(/\bpo\b/g, "PO")
      }));
      setInvoicesIng(pieData);
    }
  }, [data?.total_by_complexity]);

  useEffect(() => {
    if (data?.by_business_unit?.length)
      setDataByBusinessUnit(data?.by_business_unit);
  }, [data?.by_business_unit])

  useEffect(() => {
    if (data?.by_supplier?.length)
      setInvComplexBySupplier(data?.by_supplier);
  }, [data?.by_supplier])

  useEffect(() => {
    if (data?.exceptions?.length){

      setInvComplexExeptionList(data?.exceptions);
    }else{
      setInvComplexExeptionList([]);
    }
  }, [data?.exceptions])

  useEffect(() => {
    if (data?.trend?.length) {
      setInvComplexRptTrends(data?.trend)
    }
  }, [data?.trend])

  const handleChange = (event) => {
    setInvComplexityRptFilters((prev) => {
      return {
        ...prev,
        [event.target.name]: event.target.value
      }
    })

  };

  const handleApply = () => {
    onApplyInvComplexRptFilter(invComplexityRptFilters)
  }

  const handleReset = () => {
    setInvComplexityRptFilters({
      complexity_type: "",
      invoice_status: ""
    })

    onClear && onClear(0);
  };
  const coloursByStatusDonut = [ 
    '#3C2676','#5D3EA8','#8F7DC7','#B6A9DB', 
  ];

  const seriesByComplexity = [
    { dataKey: 'Single_Po', stack: 'complexity', label: 'Single PO', color: '#3C2676' },
    { dataKey: 'Multi_Po', stack: 'complexity', label: 'Multi PO', color: '#5D3EA8' },
    { dataKey: 'Non_Po', stack: 'complexity', label: 'Non PO', color:'#8F7DC7'  },
    { dataKey: 'Yet_To_Be_Extracted', stack: 'complexity', label: 'Yet to be extracted', color: '#B6A9DB' },
  ];
  const seriesForInvComplexSupplier = [
    { dataKey: 'Single_Po', stack: 'complexity', label: 'Single PO', color: '#3C2676' },         
    { dataKey: 'Multi_Po', stack: 'complexity', label: 'Multi PO', color: '#5D3EA8' },
    { dataKey: 'Non_Po', stack: 'complexity', label: 'Non PO', color:'#8F7DC7'  },            
    { dataKey: 'Yet_To_Be_Extracted', stack: 'complexity', label: 'Yet to be extracted', color:  '#B6A9DB'},
  ];

  const capitalizeWords = (value: string) =>
    value
      ? value
        .replace(/_/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
      : "";
  const columns = [
    { field: 'invoiceid', headerName: 'Invoice ID', flex: 1, align: 'left', headerAlign: 'left' },
    {
      field: 'supplier', headerName: 'Supplier name', flex: 1, align: 'left', headerAlign: 'left',
      valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
    },
    {
      field: 'business_unit', headerName: 'Business unit', flex: 1, align: 'left', headerAlign: 'left',
      valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
    },
    {
      field: 'status', headerName: 'Status', flex: 1, align: 'left', headerAlign: 'left',
      valueFormatter: ({ value }: { value: string }) => capitalizeWords(value)
    },
    {
      field: 'complexity', headerName: 'Complexity', flex: 1, align: 'left', headerAlign: 'left',
      valueFormatter: ({ value }: { value: string }) => capitalizeWords(value).replace(/\bPo\b/g, "PO")
    },
    { field: 'age', headerName: 'Age (in days)', type: 'number', flex: 1, align: 'left', headerAlign: 'left' },
  ] as const;

  return (
    <div style={{ backgroundColor:  'var(--background-off-white)' }}>
      {isPending ? <Box sx={{display: 'flex', justifyContent: 'center'}}><CircularProgress sx={{ margin: '0 auto', mt: "2%"}}/></Box>: <>
      <Box sx={{ width: "100%", background:  'var(--background-off-white)', borderRadius: "8px", mb: 4 }}>
        <Grid container spacing={3} alignItems="flex-end" >
          <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', justifyContent: 'center',gap:"8px", alignItems: 'start', flexDirection: "column", width: '20%' }}>
            <InputLabel sx={{fontSize:'12px',fontWeight:'400'}}>Complexity type</InputLabel>
            <FormControl
              fullWidth
              sx={{ height: '40px' }}
              size="small"
            >
              <Select
                multiple
                value={invComplexityRptFilters.complexity_type}
                onChange={event => handleChange(event)}
                size="small"
                name="complexity_type"
                sx={{ background: 'var(--white)', height: '38px', borderRadius: '6' }}
                displayEmpty
                renderValue={(selected) => selected.length ? selected.map(opt => opt.replaceAll("_"," ")).join(",") : <span style={{ color: 'var(--brand-text-default)',fontSize:'14px' }}>Select complexity </span>}
              >
                {complexityTypes.map((type) => (
                  <MenuItem value={type.value} key={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex',gap:"8px", justifyContent: 'center', alignItems: 'start', flexDirection: "column",  width: '20%'}}>
            <InputLabel sx={{fontSize:'12px',fontWeight:'400'}}>Invoice status</InputLabel>
            <FormControl fullWidth sx={{ height: '40px' }} size="small">
              <Select
              multiple
                value={invComplexityRptFilters.invoice_status}
                name="invoice_status"
                onChange={handleChange}
                size="small"
                sx={{ background: 'var(--white)', height: '38px', borderRadius: '6'  }}
                displayEmpty
                renderValue={selected =>
                  selected.length
                    ? selected.map(opt => opt.replaceAll("_"," ")).join(",")
                    : <span style={{ color: 'var(--brand-text-default)',fontSize:'14px' }}>Select invoice status</span>
                }
              >
                {invoiceStatusListData.map((value) => (
                  <MenuItem value={value} key={value}>
                    {value.replaceAll("_"," ").replace(/\bErp\b/g, "ERP")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={4} container alignItems="center" spacing={1} sx={{mb: '2px'}}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                sx={{minWidth: '102px', fontSize: '14px', padding: '10px 12px', fontWeight: 600, lineHeight: '18px', height: 'auto'}}
                onClick={handleApply}
                disabled={!invComplexityRptFilters.complexity_type && !invComplexityRptFilters.invoice_status}
              >
                Apply filters
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleReset}  sx={{ fontSize: '14px', fontWeight: 600 }}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: '32px', width: '100%' }}>
          <CustomPieChart data={invoicesIngByStatus} title="Daily invoice Complexity (by status)" colours={coloursByStatusDonut} />
            
          <Box sx={{ width: "65%" }}>
            <StackedBarChart
              data={dataByBusinessUnit}
              series={seriesByComplexity}
              xAxisKey="business_unit"
              xAxisLabel="Business unit"
              yAxisLabel="Invoices"
              title="Invoice complexity by business unit"
            />
          </Box>
        </Box>

        <StackedBarChart
          data={invComplexBySupplier}
          series={seriesForInvComplexSupplier}
          xAxisKey="supplier"
          xAxisLabel="Suppliers"
          yAxisLabel="Invoices"
          title="Invoice complexity by suppliers"
        />
        <TrendsLineChartForInvoiceComplexity dataset={invComplexRptTrends} title="Invoice complexity trend" xAxisLabel="Date" />
        <AgingTable rows={invComplexExeptionList?.length ? invComplexExeptionList : []} columns={columns} loading={isPending} title={"Complex invoices exception list"} />
      </div>
      </>
      }
    </div>
  );
}
